import { useLingui } from "@lingui/react/macro";
import { Box, CircularProgress, CssBaseline, ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { createContext, use, useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import ErrorPage from "@/components/ErrorPage";
import { config } from "@/config/env";
import { useIframeResize } from "@/hooks/useIframeResize";
import { api } from "@/services/api";
import { createWidgetTheme } from "@/theme/mui-theme";
import type { ThemeName } from "@/theme/presets";
import type { ThemeOverrides } from "@/theme/types";
import type { AffiliateBookingWidgetAppearance, CampgroundDataResponse } from "@/types/api";
import { setAdminParentOrigin } from "@/utils/adminWidget";
import { analytics } from "@/utils/analytics";
import { getDateFnsLocale } from "@/utils/booking";
import { logError } from "@/utils/logError";

// --- Types ---

type AppearanceConfig = {
  theme?: ThemeName;
  showCampnerdLabel?: boolean;
  affiliateLogoUrl?: string;
  imprintUrl?: string;
  privacyPolicyUrl?: string;
} & ThemeOverrides;

interface WidgetConfig {
  token?: string;
  campgroundSlug?: string;
  aid?: string;
  language?: string;
  appearance?: AppearanceConfig;
  demoMode?: boolean;
  trackingConsent?: boolean;
}

interface AppContextValue {
  language: string;
  campgroundSlug: string;
  aid: string | null;
  theme: ReturnType<typeof createWidgetTheme>;
  showCampnerdLabel: boolean;
  affiliateLogoUrl: string | undefined;
  imprintUrl: string | undefined;
  privacyPolicyUrl: string | undefined;
  configReady: boolean;
  campgroundData: CampgroundDataResponse;
  currencyCode: string;
  setCurrencyCode: (code: string) => void;
  demoMode: boolean | undefined;
  isEmbedded: boolean;
  usesExternalConsent: boolean;
}

// --- SessionStorage cache ---

const CAMPGROUND_CACHE_KEY = "campnerd_campground_data";
const CURRENCY_STORAGE_KEY = "campnerd_currency";

interface CachedCampgroundData {
  language: string;
  campgroundSlug: string;
  data: CampgroundDataResponse;
}

function getCachedCampgroundData(
  language: string,
  campgroundSlug: string,
): CampgroundDataResponse | null {
  const stored = sessionStorage.getItem(CAMPGROUND_CACHE_KEY);
  if (!stored) return null;
  try {
    const cached: CachedCampgroundData = JSON.parse(stored);
    if (cached.language === language && cached.campgroundSlug === campgroundSlug)
      return cached.data;
    return null;
  } catch (error) {
    logError(error);
    return null;
  }
}

function setCachedCampgroundData(
  language: string,
  campgroundSlug: string,
  data: CampgroundDataResponse,
) {
  try {
    sessionStorage.setItem(
      CAMPGROUND_CACHE_KEY,
      JSON.stringify({ language, campgroundSlug, data }),
    );
  } catch (error) {
    logError(error);
  }
}

function snakeToCamel(s: string): string {
  return s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

function parseAffiliateBookingWidgetAppearance(
  raw: AffiliateBookingWidgetAppearance | string,
): AppearanceConfig {
  const t: AffiliateBookingWidgetAppearance = typeof raw === "string" ? JSON.parse(raw) : raw;
  return Object.fromEntries(
    Object.entries(t)
      .filter(([, v]) => Boolean(v))
      .map(([k, v]) => [snakeToCamel(k), v]),
  ) as AppearanceConfig;
}

// --- Context ---

export const AppConfigContext = createContext<AppContextValue | undefined>(undefined);

export function AppConfigProvider({ children }: { children: React.ReactNode }) {
  const params = useParams<{ language: string; campground: string }>();
  const [searchParams] = useSearchParams();
  const { i18n, t } = useLingui();
  const [token, setToken] = useState<string | null>(searchParams.get("token"));
  const isAdmin = searchParams.get("mcadmin") === "1";
  const isIframe = window.self !== window.top;

  const language = params.language!;
  const campgroundSlug = params.campground!;

  // --- Appearance ---
  const [localAppearance, setLocalAppearance] = useState<AppearanceConfig | undefined>();
  const [affiliateAppearance, setAffiliateAppearance] = useState<AppearanceConfig | undefined>();
  const [configReady, setConfigReady] = useState(!isIframe);
  const [aid, setAid] = useState<string | null>(searchParams.get("aid") ?? config.defaultAid);
  const [demoMode, setDemoMode] = useState<boolean | undefined>(isAdmin ? false : undefined);
  const [usesExternalConsent, setUsesExternalConsent] = useState(false);

  useEffect(() => {
    if (!isIframe) return;
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      if (event.data?.type === "campnerd.init") {
        const widgetConfig: WidgetConfig = event.data.config;
        if (!widgetConfig || typeof widgetConfig !== "object") return;
        if (isAdmin) setAdminParentOrigin(event.origin);
        setToken(typeof widgetConfig.token === "string" ? widgetConfig.token : null);
        if (widgetConfig.appearance) setLocalAppearance(widgetConfig.appearance);
        if (widgetConfig.aid) setAid(widgetConfig.aid);
        if (typeof widgetConfig.demoMode === "boolean") setDemoMode(widgetConfig.demoMode);
        if (typeof widgetConfig.trackingConsent === "boolean") {
          setUsesExternalConsent(true);
          analytics.configureConsentRequirement(true);
          analytics.setTrackingConsent(widgetConfig.trackingConsent ? "granted" : "denied", {
            persist: false,
          });
        }
        setConfigReady(true);
        return;
      }

      if (
        event.data?.type === "campnerd.tracking-consent" &&
        typeof event.data.allowed === "boolean"
      ) {
        const trackingWasAllowed = analytics.getTrackingConsent() === "granted";
        setUsesExternalConsent(true);
        analytics.configureConsentRequirement(true);
        analytics.setTrackingConsent(event.data.allowed ? "granted" : "denied", {
          persist: false,
        });
        if (event.data.allowed && !trackingWasAllowed) analytics.page(window.location.pathname);
      }
    };
    window.addEventListener("message", handleMessage);
    // Signal to the parent that the listener is registered and we're ready to
    // receive campnerd.init. This avoids a race where the parent sends the
    // message via iframe.onload before React's useEffect has run.
    window.parent.postMessage({ type: "campnerd.ready" }, "*");
    return () => window.removeEventListener("message", handleMessage);
  }, [isIframe, isAdmin]);

  useEffect(() => {
    analytics.setCampground(campgroundSlug);
  }, [campgroundSlug]);

  useEffect(() => {
    analytics.setAid(aid);
  }, [aid]);

  const mergedAppearance = useMemo<AppearanceConfig>(
    () =>
      ({
        ...affiliateAppearance,
        ...localAppearance,
      }) as AppearanceConfig,
    [affiliateAppearance, localAppearance],
  );
  const {
    showCampnerdLabel = false,
    affiliateLogoUrl,
    imprintUrl,
    privacyPolicyUrl,
  } = mergedAppearance;
  const theme = useMemo(() => {
    const {
      theme: themeName = "campnerd",
      primaryColor,
      primaryContrastText,
      accentColor,
      backgroundColor,
      borderRadius,
    } = mergedAppearance;
    const overrides: ThemeOverrides = {
      primaryColor,
      primaryContrastText,
      accentColor,
      backgroundColor,
      borderRadius,
    };
    const hasOverrides = Object.values(overrides).some(Boolean);
    return createWidgetTheme(themeName as ThemeName, hasOverrides ? overrides : undefined);
  }, [mergedAppearance]);

  useIframeResize();

  // --- Campground Data ---
  const [campgroundData, setCampgroundData] = useState<CampgroundDataResponse | null>(() =>
    token || isAdmin ? null : getCachedCampgroundData(language, campgroundSlug),
  );
  const [campgroundLoading, setCampgroundLoading] = useState(false);
  const [campgroundError, setCampgroundError] = useState<Error | null>(null);

  useEffect(() => {
    if (!campgroundSlug || !configReady) return;

    api.setToken(token);
    api.setCampground(campgroundSlug);
    api.setLanguage(language);
    api.setAid(aid);
    if (typeof demoMode === "boolean") api.setDemoMode(demoMode);
    i18n.activate(language);

    const draftToken = new URLSearchParams(window.location.search).get("draftbooking");
    const cached = token || isAdmin ? null : getCachedCampgroundData(language, campgroundSlug);
    if (cached && !draftToken) {
      setCampgroundData(cached);
      if (cached.affiliate_booking_widget_appearance)
        setAffiliateAppearance(
          parseAffiliateBookingWidgetAppearance(cached.affiliate_booking_widget_appearance),
        );
      return;
    }

    const fetchData = async () => {
      setCampgroundLoading(true);
      setCampgroundError(null);
      try {
        const fetchParams: Record<string, string> = draftToken ? { draftbooking: draftToken } : {};
        const response = await api.getCampgroundData(language, fetchParams);
        setCampgroundData(response);
        if (response.affiliate_booking_widget_appearance)
          setAffiliateAppearance(
            parseAffiliateBookingWidgetAppearance(response.affiliate_booking_widget_appearance),
          );
        if (response.available !== false && !token && !isAdmin) {
          const { draft_booking_data: _, ...dataToCache } = response;
          setCachedCampgroundData(language, campgroundSlug, dataToCache);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logError(error);
        setCampgroundError(error);
      } finally {
        setCampgroundLoading(false);
      }
    };

    fetchData();
  }, [language, campgroundSlug, i18n, aid, configReady, demoMode, token, isAdmin]);

  // --- Currency ---
  const [currencyCode, setCurrencyCodeState] = useState<string>(
    () => localStorage.getItem(CURRENCY_STORAGE_KEY) || campgroundData?.campground?.currency || "",
  );

  useEffect(() => {
    if (campgroundData && !currencyCode && campgroundData.campground?.currency) {
      setCurrencyCodeState(campgroundData.campground.currency);
    }
  }, [campgroundData, currencyCode]);

  const setCurrencyCode = useCallback((code: string) => {
    setCurrencyCodeState(code);
    try {
      localStorage.setItem(CURRENCY_STORAGE_KEY, code);
    } catch (error) {
      logError(error);
    }
  }, []);

  // --- Locale  ---
  const dateFnsLocale = useMemo(() => getDateFnsLocale(i18n.locale), [i18n.locale]);

  const contextValue = useMemo<AppContextValue | undefined>(() => {
    if (!campgroundData) return undefined;
    return {
      language,
      campgroundSlug,
      aid,
      theme,
      showCampnerdLabel,
      affiliateLogoUrl,
      imprintUrl,
      privacyPolicyUrl,
      configReady,
      campgroundData,
      currencyCode,
      setCurrencyCode,
      demoMode,
      isEmbedded: isIframe,
      usesExternalConsent,
    };
  }, [
    language,
    campgroundSlug,
    aid,
    theme,
    showCampnerdLabel,
    affiliateLogoUrl,
    imprintUrl,
    privacyPolicyUrl,
    configReady,
    campgroundData,
    currencyCode,
    setCurrencyCode,
    demoMode,
    isIframe,
    usesExternalConsent,
  ]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
        {campgroundError || campgroundData?.available === false ? (
          <ErrorPage
            message={t`This campground is not available for booking at the moment.`}
            onRefresh={() => {
              sessionStorage.removeItem(CAMPGROUND_CACHE_KEY);
              location.reload();
            }}
          />
        ) : !campgroundData || campgroundLoading || !configReady ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={isIframe ? "200px" : "100dvh"}
          >
            <CircularProgress sx={{ color: "black" }} />
          </Box>
        ) : (
          <AppConfigContext value={contextValue}>{children}</AppConfigContext>
        )}
      </LocalizationProvider>
    </ThemeProvider>
  );
}

// --- Hooks ---

function useAppContext(): AppContextValue {
  const ctx = use(AppConfigContext);
  if (!ctx) throw new Error("Must be used within AppConfigProvider");
  return ctx;
}

export function useAppConfig() {
  const {
    language,
    campgroundSlug,
    aid,
    theme,
    showCampnerdLabel,
    affiliateLogoUrl,
    imprintUrl,
    privacyPolicyUrl,
    configReady,
    isEmbedded,
    usesExternalConsent,
  } = useAppContext();
  return {
    language,
    campgroundSlug,
    aid,
    theme,
    showCampnerdLabel,
    affiliateLogoUrl,
    imprintUrl,
    privacyPolicyUrl,
    configReady,
    isEmbedded,
    usesExternalConsent,
  };
}

export function useCampground() {
  const { campgroundData } = useAppContext();
  const campsiteTypes = useMemo(() => {
    const existing = new Set(campgroundData.campground.campsites.map((c) => c.campsite_type));
    return campgroundData.campsite_types.filter(([value]) => existing.has(value));
  }, [campgroundData]);
  return { data: campgroundData, campsiteTypes };
}

export function useCurrency() {
  const { currencyCode, setCurrencyCode, campgroundData } = useAppContext();
  return { currencyCode, setCurrencyCode, currencies: campgroundData.currencies };
}
